import { useState } from "react";
import open from "../../assets/settings/expansion-open.svg";
import close from "../../assets/settings/expansion-close.svg";
import { Image } from "../Image";
import { Flex } from "../Flex";
import { Box } from "../Box";
import styled, { keyframes } from "styled-components";
import { Paragraph } from "../Paragraph";

type ExpansionPanelProps = {
  title?: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  onToggle?: (expanded: boolean) => void;
  /** Where the expand/collapse chevron sits relative to the title. Defaults to "end" (existing behaviour). */
  iconPosition?: "start" | "end";
  /** Vertical padding on the header row. Defaults to none (existing behaviour). */
  headerPy?: number | string;
  /** Renders a divider line below the header row. Defaults to false (existing behaviour). */
  showHeaderDivider?: boolean;
  /** Top margin on the expanded content. Defaults to 2 (existing behaviour). */
  contentMt?: number | string;
};

const expansionAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;
const ExpansionPanelContainer = styled.div<ExpansionPanelProps>`
  overflow: hidden; /* Prevent content overflow when closed */
  transition: height 0.3s ease-in-out, opacity 0.3s ease-in-out; // Combined transition

  &.expanded {
    animation: ${expansionAnimation} 0.3s ease-in-out; /* Apply animation on toggle */
  }
`;

export const ExpansionPanel = (props: ExpansionPanelProps) => {
  const {
    title,
    children,
    defaultExpanded = false,
    onToggle,
    iconPosition = "end",
    headerPy,
    showHeaderDivider = false,
    contentMt = 2,
  } = props;
  const [expanded, setExpanded] = useState(defaultExpanded);

  const titleContent =
    typeof title === "string" ? <Paragraph>{title}</Paragraph> : title;

  const toggleExpansion = () => {
    setExpanded((prev) => {
      const next = !prev;
      onToggle?.(next);
      return next;
    });
  };

  const icon = (
    <Flex alignItems="center">
      {expanded ? (
        <Image src={close} alt="close" height="26px" width="26px" />
      ) : (
        <Image src={open} alt="open" height="26px" width="26px" />
      )}
    </Flex>
  );

  return (
    <ExpansionPanelContainer className={expanded ? "expanded" : ""}>
      <Flex
        gap="12"
        py={headerPy}
        alignItems="center"
        onClick={toggleExpansion}
        borderBottom={showHeaderDivider ? "1px solid" : undefined}
        borderBottomColor={showHeaderDivider ? "gray.10" : undefined}
        style={{ cursor: "pointer", justifyContent: "space-between" }}
      >
        {iconPosition === "start" && icon}
        <Box width={"100%"}>{titleContent}</Box>
        {iconPosition === "end" && icon}
      </Flex>

      {expanded && <Box mt={contentMt}>{children}</Box>}
    </ExpansionPanelContainer>
  );
};
